import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { date, plural } from '@frameio/i18n';
import { getLink } from '@frameio/shared/routing';
import {
  Button2 as Button,
  mergeProps,
  Text,
  theme,
  Tooltip,
  useEmulateButton,
} from '@frameio/vapor';
import {
  IconCog,
  IconLink,
  IconLock,
  IconNotifications,
} from '@frameio/vapor-icons';
import { GLPYHS } from '../../../../constants';
import { useTrackActionedOnResults } from '../../../../shared/contexts';
import { useProjectResultFieldMatchesV2Fragment } from '../../../../shared/gql/generated/QuickSearchProjectResultV2';
import { useQuickSearchProjectV2Fragment as useQuickSearchProjectFragment } from '../../../../shared/gql/generated/QuickSearchProjectV2';
import { useProjectActions } from '../../../../shared/hooks';
import {
  getMatchTypeTrackingValues,
  getProjectStorage,
  getResultSourceFromSectionId,
} from '../../../../shared/utils';
import { useQuickSearchAppliedFilterTypes } from '../../../hooks';
import { getActiveListItemDetails } from '../../../utils';
import TEXT from './generated/TEXT/ProjectItemDetailQS';
import { ItemDetailContainer } from './ItemDetailContainer';
import { ItemDetailMeta } from './ItemDetailMeta';
import styles from './ProjectItemDetail.module.css';
import { ItemDetailAvatar, ProjectAvatarStyled } from './shared';

const PROJECT_AVATAR_SIZE = 160;

TEXT.DEFINE({
  /** Button label to copy project URL */
  copyUrl: 'Copy URL',
  /** Label for restricted/private project */
  restricted: 'Restricted',
  /** Button label to open project settings */
  projectSettings: 'Project Settings',
  /** Tooltip text indicating notifications are enabled */
  notifsTooltip: 'Notifications are enabled',
  /** Label for archived/inactive project */
  archived: 'Inactive',
  /** Accessibility label for opening project */
  avatarLabel: ({ name }: { name: string }): string => `Open ${name}`,
  /** Member count display with pluralization */
  memberCountLabel: ({ count = 0 }: { count?: number }): string =>
    plural(count, `${count} members`, '1 member'),
  /** Formats the date with the medium format (i.e. Jan 15, 2026) */
  getFormattedDate: (dateValue: Date): string => {
    return `${date(dateValue, 'medium')}`;
  },
});

type ProjectItemDetailProps = {
  focusedKey: string | null;
  onNavigate?: (href: string) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
};

export const ProjectItemDetail = ({
  focusedKey,
  onNavigate,
  onScroll,
}: ProjectItemDetailProps) => {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();
  const trackActionedOnResults = useTrackActionedOnResults();

  const {
    itemId: projectId = '',
    itemPosition: resultPosition = 1,
    sectionId,
  } = getActiveListItemDetails(focusedKey ? String(focusedKey) : null);

  const { data: projectData } = useQuickSearchProjectFragment({
    from: { __typename: 'Project', id: projectId },
  });

  const {
    data: { matches },
  } = useProjectResultFieldMatchesV2Fragment({
    from: { __typename: 'QuickSearchProjectResult', id: projectId },
  });

  const matchType = useMemo(
    () => getMatchTypeTrackingValues(matches?.nodes ?? []),
    [matches?.nodes]
  );

  const {
    archivedAt,
    imageUrl,
    insertedAt,
    name = '',
    owner,
    private: isProjectPrivate,
    notificationsEnabled,
    storage: storageCount,
    totalCount: totalAssetCount,
    uniqueMemberCount: memberCount,
    workspace,
  } = projectData;

  const createdAt = insertedAt
    ? TEXT.getFormattedDate(new Date(insertedAt))
    : null;
  const isArchived = Boolean(archivedAt);
  const isPrivate = Boolean(isProjectPrivate);
  const hasNotifications = Boolean(notificationsEnabled);

  const resultSource = getResultSourceFromSectionId(sectionId);
  const { handleCopyLink, handleOpenProjectSettings } = useProjectActions({
    appliedFilterTypes,
    resultPosition,
    projectId,
    resultSource,
    workspaceId: workspace?.id,
  });

  const handleOpenProject = useCallback(() => {
    trackActionedOnResults?.({
      action: 'OpenInSource',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType: 'Project',
      webSource: 'Modal',
    });
    const href = getLink({
      page: 'folder',
      projectId,
    });
    router.push(href);
    onNavigate?.(href);
  }, [
    appliedFilterTypes,
    matchType,
    onNavigate,
    projectId,
    resultPosition,
    resultSource,
    router,
    trackActionedOnResults,
  ]);

  const titleRef = useRef<HTMLDivElement>(null);
  const { bind } = useEmulateButton(titleRef, 'link');

  return (
    <ItemDetailContainer
      actionsComponent={
        <div className={styles.actionsRow}>
          <Button
            iconBefore={<IconLink />}
            onPress={handleCopyLink}
            size="small"
            variant="secondary"
          >
            {TEXT.copyUrl}
          </Button>
          <Button
            iconBefore={<IconCog />}
            onPress={handleOpenProjectSettings}
            size="small"
            variant="secondary"
          >
            {TEXT.projectSettings}
          </Button>
        </div>
      }
      imageComponent={
        <ItemDetailAvatar
          ref={ref}
          aria-label={TEXT.avatarLabel({ name })}
          display="inline-block"
          height={`${PROJECT_AVATAR_SIZE}px`}
          itemType="project"
          onClick={handleOpenProject}
          position="relative"
          width={`${PROJECT_AVATAR_SIZE}px`}
        >
          <ProjectAvatarStyled
            $isArchived={isArchived}
            id={projectId}
            size={PROJECT_AVATAR_SIZE}
            src={imageUrl ?? ''}
            title={name ?? ''}
          />
          <div className={styles.badgesRow}>
            <Notify
              enabled={hasNotifications}
              notificationsEnabledText={TEXT.notifsTooltip}
            />
            <Private enabled={isPrivate} privateTooltipText={TEXT.restricted} />
            <Archived enabled={isArchived} />
          </div>
        </ItemDetailAvatar>
      }
      metaComponent={
        <ItemDetailMeta
          itemMeta={{
            workspace: workspace?.name,
            dateCreated: createdAt,
            storage: getProjectStorage(storageCount),
            items: totalAssetCount,
            ...(archivedAt && { status: TEXT.archived }),
            ...(isPrivate && { access: TEXT.restricted }),
          }}
        />
      }
      onScroll={onScroll}
      titleComponent={
        <div
          ref={titleRef}
          aria-label={TEXT.avatarLabel({ name })}
          className={styles.titleBox}
          {...mergeProps({ onClick: handleOpenProject }, bind)}
        >
          <Text
            enableTooltip={true}
            style={{ width: '100%' }}
            textAlign="center"
            truncate={2}
            variant="header"
          >
            {name}
          </Text>
          <div className={styles.titleMetaRow}>
            <Text
              color="tertiary"
              enableTooltip={true}
              truncate={true}
              variant="secondary"
            >
              {owner?.name}
            </Text>
            <Text color="tertiary" variant="secondary">
              {GLPYHS.bullet}
            </Text>
            <Text
              className={styles.nonShrinking}
              color="tertiary"
              variant="secondary"
            >
              {TEXT.memberCountLabel({ count: memberCount ?? undefined })}
            </Text>
          </div>
        </div>
      }
    />
  );
};

export const Archived = ({ enabled }: { enabled: boolean }) => {
  return (
    enabled && (
      <div className={styles.pillContainer}>
        <Text
          className={styles.pillText}
          color="primary"
          fontWeight="semiBold"
          variant="caption"
        >
          {TEXT.archived}
        </Text>
      </div>
    )
  );
};

export const Private = ({
  enabled,
  privateTooltipText = 'Restricted',
}: {
  privateTooltipText?: string;
  enabled: boolean;
}) => {
  return (
    enabled && (
      <Tooltip title={privateTooltipText}>
        <div className={styles.pillContainer}>
          <IconLock color={theme.colors.components.badge.icon} size={16} />
        </div>
      </Tooltip>
    )
  );
};

export const Notify = ({
  enabled,
  notificationsEnabledText = 'Notifications Enabled',
}: {
  enabled: boolean;
  notificationsEnabledText?: string;
}) => {
  return (
    enabled && (
      <Tooltip title={notificationsEnabledText}>
        <div className={styles.pillContainer}>
          <IconNotifications
            color={theme.colors.components.badge.icon}
            data-testid="project-card-notifications-enabled-icon"
            size={16}
          />
        </div>
      </Tooltip>
    )
  );
};
